// App.jsx
// Main app wrapper with auth state management
//
// AUTH FLOW DOCUMENTATION:
// ========================
// This file handles several authentication scenarios:
//
// 1. NORMAL LOGIN: User signs in with email/password
//    - onAuthStateChange fires 'SIGNED_IN'
//    - Load user data and show CareerAgent
//
// 2. NEW SIGNUP: User creates account
//    - User must confirm email first
//    - After confirmation, treated as normal login
//
// 3. PASSWORD RECOVERY (for unconfirmed or confirmed users):
//    - User clicks "Forgot Password" and receives email
//    - Clicking email link triggers 'PASSWORD_RECOVERY' event
//    - We check if user has existing profile in database:
//      a) HAS PROFILE: Show ProfileConfirmation component
//         - Display their existing data (name, role, salary)
//         - Allow them to edit and set new password
//         - This serves as their "confirmation" if they never confirmed
//      b) NO PROFILE: Show AuthComponent in 'complete-profile' mode
//         - Treat like fresh signup
//         - Collect name, role, salary info
//         - Save profile and proceed to app

import React, { useState, useEffect, useRef } from 'react';
import { supabase, onAuthStateChange, signOut, getProfile, updateProfile, getJobsSummary, getInsights, getSkillAssessments, getInterviews, getBragBookEntries, dbJobSummaryToApp, dbInsightToAppInsight, dbProfileToAppProfile, dbInterviewToAppInterview, dbBragBookEntryToApp } from './supabaseClient';
import AuthComponent from './AuthComponent';
import CareerAgent from './CareerAgent';
import MarketingPage from './MarketingPage';
import ProfileConfirmation from './ProfileConfirmation';

export default function App() {
  console.log('App component rendering');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialData, setInitialData] = useState(null);
  const [showMarketing, setShowMarketing] = useState(true);
  const [pendingPlan, setPendingPlan] = useState(null); // Track plan selected from marketing page

  // Recovery flow state:
  // - null: normal flow
  // - 'has-profile': user has existing profile, show ProfileConfirmation
  // - 'no-profile': user has no profile, show signup-style form
  const [recoveryMode, setRecoveryMode] = useState(null);
  const [recoveryProfile, setRecoveryProfile] = useState(null);

  // Ref to prevent duplicate data loads (React StrictMode double-invokes effects)
  const dataLoadedRef = useRef(false);

  useEffect(() => {
    let subscription = null;

    // Check current session - try localStorage first to avoid hanging getSession()
    const checkSession = async () => {
      console.log('Checking session...');

      // Check if we're returning from OAuth callback
      // Supabase can use either:
      // - Hash params (implicit flow): #access_token=...
      // - Query params (PKCE flow): ?code=...
      const hashParams = window.location.hash;
      const searchParams = window.location.search;
      const isOAuthCallback =
        (hashParams && (hashParams.includes('access_token') || hashParams.includes('error'))) ||
        (searchParams && (searchParams.includes('code=') || searchParams.includes('error=')));

      if (isOAuthCallback) {
        console.log('OAuth return detected, waiting for auth listener...', { hashParams, searchParams });
        // Don't set loading to false - auth listener will handle it
        return;
      }

      try {
        // Try to get session from localStorage directly (Supabase stores it there)
        // This avoids the hanging getSession() call
        const storageKey = `sb-pqmcckwxinhnntobonlq-auth-token`;
        const storedSession = localStorage.getItem(storageKey);

        if (storedSession) {
          try {
            const parsed = JSON.parse(storedSession);
            const session = parsed;
            console.log('Session from localStorage:', session?.user?.id ? 'found' : 'invalid');

            if (session?.user && session?.access_token) {
              // Check if token is expired
              const expiresAt = session.expires_at;
              const now = Math.floor(Date.now() / 1000);

              if (expiresAt && expiresAt > now) {
                console.log('Session valid, expires in', expiresAt - now, 'seconds');
                setUser(session.user);
                await loadUserData(session.user.id, session);
                return;
              } else {
                console.log('Session expired, clearing...');
                localStorage.removeItem(storageKey);
              }
            }
          } catch (parseErr) {
            console.error('Failed to parse stored session:', parseErr);
          }
        }

        console.log('No valid session in localStorage');
      } catch (err) {
        console.error('Session check failed:', err.message);
      } finally {
        console.log('Setting loading to false');
        setLoading(false);
      }
    };

    // Setup auth listener with error handling
    // Handles: SIGNED_IN, SIGNED_OUT, PASSWORD_RECOVERY
    try {
      const authListener = onAuthStateChange(async (event, session) => {
        console.log('Auth event:', event);

        if (event === 'PASSWORD_RECOVERY' && session?.user) {
          // User clicked password reset link
          // Check if they have an existing profile to determine flow
          console.log('Password recovery detected, checking for existing profile...');
          setUser(session.user);

          const { data: existingProfile } = await getProfile(session.user.id);

          if (existingProfile && existingProfile.name) {
            // User has profile data - show edit/confirm screen
            console.log('Existing profile found, showing confirmation screen');
            setRecoveryProfile(dbProfileToAppProfile(existingProfile));
            setRecoveryMode('has-profile');
          } else {
            // User has no profile - show signup-style form
            console.log('No profile found, showing signup form');
            setRecoveryMode('no-profile');
          }
          setLoading(false);
        } else if (event === 'SIGNED_IN' && session?.user) {
          // Normal sign in (or after recovery completes)
          if (!recoveryMode) {
            console.log('SIGNED_IN: passing session directly to loadUserData');
            setUser(session.user);

            // Check if this is an OAuth user (LinkedIn) with metadata we can use
            const userMeta = session.user.user_metadata;
            const provider = session.user.app_metadata?.provider;

            if (provider === 'linkedin_oidc' && userMeta) {
              console.log('LinkedIn OAuth user detected, checking if profile needs creation...');
              const { data: existingProfile } = await getProfile(session.user.id, session);

              if (!existingProfile?.name && userMeta.full_name) {
                // New LinkedIn user - pre-populate profile with LinkedIn data
                console.log('Creating profile from LinkedIn data:', userMeta.full_name);
                await updateProfile(session.user.id, {
                  name: userMeta.full_name || userMeta.name || '',
                  email: session.user.email || userMeta.email || '',
                  // LinkedIn headline can be used as title if available
                  title: userMeta.headline || '',
                });
              }
            }

            // Check for pending profile data from signup (saved before email confirmation)
            const pendingProfileJson = localStorage.getItem('pendingProfileData');
            if (pendingProfileJson) {
              try {
                const pendingProfile = JSON.parse(pendingProfileJson);
                // Only apply if this is the same user who signed up
                if (pendingProfile.userId === session.user.id) {
                  console.log('Found pending profile data from signup, saving now...');
                  const { userId, ...profileData } = pendingProfile;
                  await updateProfile(session.user.id, profileData);
                  console.log('Pending profile data saved successfully');
                }
                // Clear pending data regardless of whether we used it
                localStorage.removeItem('pendingProfileData');
              } catch (e) {
                console.error('Failed to process pending profile data:', e);
                localStorage.removeItem('pendingProfileData');
              }
            }

            // Pass session directly to avoid hanging getSession() call
            await loadUserData(session.user.id, session);
          }
        } else if (event === 'SIGNED_OUT') {
          console.log('SIGNED_OUT event - resetting all state');
          setUser(null);
          setInitialData(null);
          setRecoveryMode(null);
          setRecoveryProfile(null);
          setShowMarketing(true); // Return to marketing page
          dataLoadedRef.current = false; // Allow data to load on next sign-in
        }
      });
      subscription = authListener?.data?.subscription;
    } catch (err) {
      console.error('Auth listener setup failed:', err);
    }

    checkSession();

    // Safety timeout - ensure loading completes even if Supabase hangs
    const timeout = setTimeout(() => {
      console.log('Safety timeout triggered - forcing loading to false');
      setLoading(false);
    }, 5000);

    return () => {
      clearTimeout(timeout);
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  const loadUserData = async (userId, session = null) => {
    console.log('loadUserData called for userId:', userId, 'session provided:', !!session);

    // Prevent duplicate loads (React StrictMode double-invokes effects in dev)
    if (dataLoadedRef.current) {
      console.log('⚠️ Data already loaded, skipping duplicate fetch');
      return;
    }
    dataLoadedRef.current = true;

    // Timeout wrapper to prevent Supabase client hangs
    const withTimeout = async (promise, name, timeoutMs = 5000) => {
      try {
        const result = await Promise.race([
          promise,
          new Promise((_, reject) => setTimeout(() => reject(new Error(`${name} timeout`)), timeoutMs))
        ]);
        return result;
      } catch (err) {
        console.error(`⏱️ ${name} failed:`, err.message);
        return { data: null, error: err };
      }
    };

    try {
      // Load profile, jobs (summary only), and interviews in parallel
      console.log('📍 Fetching profile + jobs (summary) + interviews in parallel...');
      const [profileRes, jobsRes, interviewsRes] = await Promise.all([
        getProfile(userId, session),
        withTimeout(getJobsSummary(userId), 'Jobs', 8000),  // Summary is much faster
        withTimeout(getInterviews(userId), 'Interviews', 10000)
      ]);
      console.log('✅ Profile:', profileRes.data ? 'found' : 'null');
      console.log('✅ Jobs (summary):', jobsRes.data?.length || 0, 'of', jobsRes.totalCount || 0);
      console.log('✅ Interviews:', interviewsRes.data?.length || 0);

      const profile = profileRes.data ? dbProfileToAppProfile(profileRes.data) : null;
      const jobs = jobsRes.data ? jobsRes.data.map(dbJobSummaryToApp) : [];
      const interviews = interviewsRes.data ? interviewsRes.data.map(dbInterviewToAppInterview) : [];

      console.log('Setting initialData with profile:', profile?.name, 'jobs:', jobs.length, 'interviews:', interviews.length);
      setInitialData({
        profile,
        jobs,
        totalJobCount: jobsRes.totalCount || 0,  // For pagination
        insights: [],           // Load lazily
        interviews,
        bragBookEntries: [],    // Load lazily
        skillAssessments: {},   // Load lazily
        userId,
        subscriptionStatus: profileRes.data?.subscription_status || 'free',
      });
      setLoading(false);
    } catch (err) {
      console.error('Failed to load user data:', err);
      setInitialData({ profile: null, jobs: [], insights: [], interviews: [], bragBookEntries: [], skillAssessments: {}, userId });
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    console.log('Logout clicked - handleSignOut called');

    // Force state reset immediately - don't wait for Supabase
    setUser(null);
    setInitialData(null);
    setShowMarketing(true);
    dataLoadedRef.current = false; // Allow data to load on next sign-in
    console.log('State reset complete - showing marketing page');

    // Try Supabase signOut in background (with timeout)
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('SignOut timeout')), 3000)
      );
      const { error } = await Promise.race([signOut(), timeoutPromise]);
      if (error) {
        console.error('Supabase signOut error:', error);
      } else {
        console.log('Supabase signOut completed');
      }
    } catch (err) {
      console.warn('SignOut background cleanup failed:', err.message);
    }
  };

  const handleAuthSuccess = async (user, session = null) => {
    setUser(user);
    setLoading(true);
    await loadUserData(user.id, session);
    setLoading(false);
  };

  // Called when user completes the recovery/confirmation flow
  // This handles both:
  // - Users with existing profiles who updated their password
  // - Users without profiles who just completed signup via recovery
  const handleRecoveryComplete = async () => {
    console.log('Recovery flow completed');
    setRecoveryMode(null);
    setRecoveryProfile(null);
    setLoading(true);
    await loadUserData(user.id);
    setLoading(false);
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#fffef9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'IBM Plex Sans', sans-serif",
      }}>
        <div style={{ textAlign: 'center' }}>
          <svg width="80" height="80" viewBox="0 0 52 52" fill="none" style={{ margin: '0 auto 16px' }}>
            {/* Manifold curves with drawing animation */}
            <path
              d="M8 38 Q16 28, 22 32 T34 26"
              stroke="#3d3d3d"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              opacity="0.8"
              strokeDasharray="50"
              strokeDashoffset="0"
              style={{ animation: 'drawLine 2s ease-in-out infinite' }}
            />
            <path
              d="M6 44 Q14 32, 20 38 T32 30"
              stroke="#3d3d3d"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              opacity="0.4"
              strokeDasharray="50"
              strokeDashoffset="0"
              style={{ animation: 'drawLine 2s ease-in-out infinite 0.3s' }}
            />
            <circle cx="8" cy="38" r="2.5" fill="#d4736a" style={{ animation: 'dotPulse 1s ease-in-out infinite' }}/>
            {/* Pencil with gentle bob animation - use wrapper for animation */}
            <g style={{ animation: 'pencilBob 1.5s ease-in-out infinite', transformOrigin: '38px 14px' }}>
              <g transform="rotate(-45 38 14)">
                <rect x="24" y="11" width="20" height="6" fill="#f7c948" stroke="#d4a832" strokeWidth="0.5"/>
                <rect x="44" y="11" width="4" height="6" fill="#b8b8b8"/>
                <rect x="48" y="11.5" width="3" height="5" rx="0.5" fill="#d4736a"/>
                <polygon points="24,11 24,17 20,14" fill="#c9a87c"/>
                <polygon points="20,14 22,12.5 22,15.5" fill="#3d3d3d"/>
              </g>
            </g>
          </svg>
          <style>{`
            @keyframes dotPulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
            @keyframes drawLine {
              0% { stroke-dashoffset: 50; }
              50% { stroke-dashoffset: 0; }
              100% { stroke-dashoffset: -50; }
            }
            @keyframes pencilBob {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-3px); }
            }
          `}</style>
          <p style={{ color: '#7a7a7a', fontSize: '14px', marginTop: '8px' }}>Loading your data...</p>
        </div>
      </div>
    );
  }

  // RECOVERY FLOW: User with existing profile - show edit/confirm screen
  // This happens when user clicked password reset link and has profile data
  if (recoveryMode === 'has-profile' && user) {
    return (
      <ProfileConfirmation
        user={user}
        existingProfile={recoveryProfile}
        onComplete={handleRecoveryComplete}
        onCancel={() => {
          // User cancelled - sign them out and reset
          signOut();
          setRecoveryMode(null);
          setRecoveryProfile(null);
        }}
      />
    );
  }

  // RECOVERY FLOW: User without profile - show signup-style form
  // This happens when user clicked password reset link but never completed signup
  if (recoveryMode === 'no-profile' && user) {
    return (
      <AuthComponent
        onAuthSuccess={handleRecoveryComplete}
        recoveryMode={true}
        recoveryUser={user}
      />
    );
  }

  // Marketing page for non-authenticated users
  if (showMarketing && !user) {
    return (
      <MarketingPage
        onEnterApp={() => setShowMarketing(false)}
        onSelectPlan={(plan) => setPendingPlan(plan)}
      />
    );
  }

  // Not logged in - show auth
  if (!user) {
    return <AuthComponent onAuthSuccess={handleAuthSuccess} />;
  }

  // Logged in - show main app
  return (
    <CareerAgent
      user={user}
      initialData={initialData}
      onSignOut={handleSignOut}
      pendingPlan={pendingPlan}
      onClearPendingPlan={() => setPendingPlan(null)}
    />
  );
}
